"use client"

import { motion, Variants } from "framer-motion"
import { Button } from "@/components/ui/button"
import { ArrowRight, Zap, Check } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { CONTACT } from "@/lib/constants"
import {WhatsAppIcon} from '@/components/icon'

export function Hero() {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1], // ✅ correct cubic-bezier equivalent of easeOut
      },
    },
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#003DA5] via-[#0047AB] to-[#0052CC] min-h-[calc(100vh-80px)] flex items-center py-12 sm:py-16">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          className="absolute top-20 -left-32 w-96 h-96 bg-[#FF7A00] rounded-full mix-blend-multiply filter blur-3xl opacity-20 pointer-events-none"
          style={{ willChange: "transform" }}
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 10, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-40 -right-32 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 pointer-events-none"
          style={{ willChange: "transform" }}
          animate={{
            x: [0, -50, 0],
            y: [0, 40, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 12, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut", delay: 1 }}
        />
        <motion.div
          className="absolute bottom-20 left-1/2 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15 pointer-events-none"
          style={{ willChange: "transform" }}
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{ duration: 11, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut", delay: 2 }}
        />
      </div>

      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-0">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Left Content */}
          <motion.div className="space-y-5 lg:pr-6 relative z-10" variants={itemVariants}>
            {/* Trust Badge */}
            <motion.div
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/20 shadow-lg relative z-10"
              variants={itemVariants}
            >
                <Zap size={16} className="text-[#FF7A00]" />
                <span className="font-medium text-white">Trusted Packers & Movers</span>
            </motion.div>

            {/* Main Heading */}
            <motion.div variants={itemVariants} className="space-y-1">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block text-white">Stress-Free</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#FF7A00] via-orange-500 to-[#FF7A00]">
                  Shifting
                </span>
              </h1>
            </motion.div>

            {/* Description */}
            <motion.p
              className="text-sm sm:text-base lg:text-lg text-white/90 leading-relaxed max-w-xl"
              variants={itemVariants}
            >
              Experience hassle-free relocation with India's most trusted moving partner. Professional packing, secure
              transport, and round-the-clock support.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div className="flex flex-col sm:flex-row gap-3 relative z-10" variants={itemVariants}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
                <Link href="/get-quote" className="block w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-[#FF7A00] hover:bg-orange-600 text-white gap-2 font-bold shadow-2xl hover:shadow-orange-500/50 transition-all px-6 py-5 text-sm sm:text-base rounded-xl"
                  >
                    Get Free Quote
                    <ArrowRight size={18} />
                  </Button>
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
                <Link 
                  href={`https://wa.me/${CONTACT.phone.whatsapp}?text=Hi, I would like to get a quote for relocation services.`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full sm:w-auto"
                >
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-white/40 text-white hover:bg-white/15 backdrop-blur-md font-bold bg-white/5 gap-2 px-6 py-5 text-sm sm:text-base rounded-xl"
                  >
                    <WhatsAppIcon/>
                    WhatsApp Us
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div 
              className="grid grid-cols-3 gap-4 pt-5 border-t border-white/20" 
              variants={containerVariants}
            >
              {[
                { number: "500+", label: "Relocations" },
                { number: "98%", label: "Satisfaction" },
                { number: "24/7", label: "Support" },
              ].map((stat, index) => (
                <motion.div 
                  key={index} 
                  variants={itemVariants}
                  className="text-center lg:text-left"
                >
                  <p className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF7A00] to-orange-400 mb-1">
                    {stat.number}
                  </p>
                  <p className="text-xs text-white/80 font-semibold uppercase tracking-wide">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - Enhanced Image Card */}
          <motion.div className="relative lg:block hidden" variants={itemVariants}>
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3, type: "spring" }}
            >
              {/* Main Card */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10">
                {/* Image Container */}
                <div className="relative aspect-[1/1] lg:aspect-[4/5] bg-gradient-to-br from-teal-500 via-teal-600 to-teal-700">
                  <Image
                    src="/hero-placeholder.png"
                    alt="Happy couple with moving boxes - Professional relocation services by Bigwin"
                    fill
                    className="object-cover"
                    priority
                    quality={85}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
                  />
                  
                  {/* Subtle Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>
                </div>

                {/* Feature Badges - Positioned at Bottom */}
                <div className="absolute bottom-6 left-5 right-5 space-y-2.5 pointer-events-none">
                  {[
                    { icon: Check, text: "Premium Packing Materials" },
                    { icon: Check, text: "GPS Tracking" },
                    { icon: Check, text: "Full Insurance Coverage" },
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-2.5 bg-white/98 backdrop-blur-xl px-4 py-2.5 rounded-xl shadow-xl border border-gray-100"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.15, duration: 0.6, type: "spring" }}
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF7A00] to-orange-600 flex items-center justify-center flex-shrink-0 shadow-md">
                        <feature.icon size={14} className="text-white" strokeWidth={3} />
                      </div>
                      <span className="text-xs font-bold text-gray-800">{feature.text}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Top Badge */}
                <motion.div
                  className="absolute top-6 right-6 bg-gradient-to-r from-[#FF7A00] to-orange-600 text-white px-4 py-2 rounded-full shadow-2xl pointer-events-none"
                  initial={{ opacity: 0, scale: 0, rotate: -180 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ delay: 1, type: "spring", stiffness: 200 }}
                >
                  <p className="text-xs font-black uppercase tracking-wider">Trusted</p>
                </motion.div>
              </div>

              {/* Floating Glow Effects */}
              <motion.div
                className="absolute -top-6 -left-6 w-32 h-32 bg-[#FF7A00]/30 rounded-full blur-3xl pointer-events-none"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.4, 0.6, 0.4],
                }}
                transition={{ duration: 4, repeat: Number.POSITIVE_INFINITY }}
              />
              <motion.div
                className="absolute -bottom-6 -right-6 w-40 h-40 bg-blue-400/30 rounded-full blur-3xl pointer-events-none"
                animate={{
                  scale: [1, 1.4, 1],
                  opacity: [0.4, 0.6, 0.4],
                }}
                transition={{ duration: 5, repeat: Number.POSITIVE_INFINITY, delay: 1.5 }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
